import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import i18n from '@/utils/i18n'
import { dateFnsLocale } from '@/utils/dateLocale'

import {
  claimLead,
  createOrderFromLead,
  getInspectionLead,
  releaseLead,
  runAutotekaEnrichment,
  updateInspectionLead,
} from '@/api/inspection-leads'
import { useAuthStore } from '@/store/authStore'
import {
  leadPipelineUi,
  leadStatusLabelKey,
  leadStatusOptions,
  type CancelReasonCode,
  type InspectionLeadRow,
  type InspectionLeadStatus,
 leadIsTest } from '@/types/inspection-lead'
import { StatusBadge, TestBadge } from '@/components/inspections/LeadBadges'
import { PillToggle } from '@/components/ui/PillToggle'
import { Breadcrumbs } from '@/components/layout/Breadcrumbs'
import { DetailHeaderSkeleton, SectionSkeleton } from '@/components/ui/Skeleton'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { LeadRelationBadge } from '@/components/inspections/LeadRelationBadge'
import { LeadCommunicationsSection } from '@/components/inspections/LeadCommunicationsSection'
import { CarlinkAiSummary } from '@/components/inspections/CarlinkAiSummary'
import { CancelLeadDialog } from '@/components/inspections/CancelLeadDialog'
import { LeadActivitySection } from '@/components/inspections/LeadActivitySection'
import { CarlinkAuctionCard } from '@/components/inspections/CarlinkAuctionCard'
import { TeamChatFeed } from '@/components/inspections/TeamChatFeed'
import { LeadWorkPalette } from '@/components/inspections/LeadWorkPalette'
import { LeadPaymentCard } from '@/components/inspections/LeadPaymentCard'
import { LeadDispatcherComments } from '@/components/inspections/LeadDispatcherComments'
import { LeadPartnerSection } from '@/components/inspections/LeadPartnerSection'
import { LeadAvitoApiCalls } from '@/components/inspections/LeadAvitoApiCalls'
import { AssignedBadge, CarlinkBadge } from '@/components/inspections/LeadHeaderBadges'
import { LiveAgo } from '@/components/inspections/LiveAgo'
import { AutotekaControl, AutotekaPdfButton, CopyAutotekaLinkButton } from '@/components/inspections/LeadAutoteka'
import {
  diffLeadDraft,
  hasAutotekaVehicleId,
  humanizeEnrichError,
  isAutotekaWaiting,
  leadCardCls as cardCls,
  toLeadDraft,
  type LeadDraft,
} from '@/components/inspections/leadDetail'
import { formatPhoneInput } from '@/utils/phone'
import { formatMoscowDateTime } from '@/utils/format'
import { statusLabelRu } from '@/utils/status-transitions'
import type { OrderStatus } from '@/types/enums'

/**
 * /inspection-leads/:id — карточка лида.
 *
 * Layout по стандарту OrderDetailPage: full-width, breadcrumbs + header
 * c h1+StatusBadge+actions, сетка 5 колонок (3/2). Левая колонка —
 * редактируемые поля по секциям, правая — палитра действий, заказ, торги,
 * активность. Какие секции и действия есть у воронки — LEAD_PIPELINE_UI.
 */
export function InspectionLeadDetailPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams<{ id: string }>()
  const qc = useQueryClient()

  const leadQ = useQuery({
    queryKey: ['inspection-lead', id],
    queryFn: () => getInspectionLead(id),
    enabled: !!id,
    // Обновления приходят через Supabase Realtime: useOrdersRealtime
    // подключён глобально в App.tsx; миграция 20260601170000 добавила
    // inspection_leads + inspection_lead_changes в publication. UPDATE
    // лида / INSERT в журнал → invalidate cache → query рефетчит.
    refetchOnWindowFocus: true,
    // Пока ждём Автотеку (по госномеру — до 45 мин), подстраховка к Realtime —
    // переспрашиваем раз в 15 сек.
    refetchInterval: (q) => (q.state.data && isAutotekaWaiting(q.state.data) ? 15_000 : false),
  })

  if (leadQ.isLoading && !leadQ.data) {
    return (
      <div className="w-full max-w-none px-6 py-4 lg:px-8">
        <Breadcrumbs
          items={[{ label: t('leads.title'), to: '/inspection-leads' }, { label: t('leads.detail.breadcrumbLead') }]}
        />
        <DetailHeaderSkeleton />
        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-5">
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-3">
            <SectionSkeleton lines={3} />
            <SectionSkeleton lines={5} />
            <SectionSkeleton lines={3} />
          </div>
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
            <SectionSkeleton lines={2} />
            <SectionSkeleton lines={2} />
            <SectionSkeleton lines={4} />
          </div>
        </div>
      </div>
    )
  }

  if (!leadQ.data) {
    return (
      <div className="w-full max-w-none px-6 py-4 lg:px-8">
        <Breadcrumbs
          items={[{ label: t('leads.title'), to: '/inspection-leads' }, { label: t('leads.detail.breadcrumbLead') }]}
        />
        <div className="p-8 text-[var(--stryzh-color-text-secondary)]">
          {leadQ.error ? (leadQ.error as Error).message : t('leads.detail.notFound')}
        </div>
      </div>
    )
  }

  return (
    // НЕ ставим key={updated_at}: после save query инвалидируется → новый
    // updated_at → компонент пересоздаётся → все локальные состояния (включая
    // открытую SMS-модалку из палитры) сбрасываются. Это вызывало race:
    // клиент нажимает «Отправить SMS», beforeOpen save'ит draft, рефетч query
    // приходит ровно в момент setOpen(true), компонент пересоздаётся, диалог
    // мгновенно исчезает. draft и так синхронизируется естественно (после
    // save поля в lead = поля в draft → dirty=false).
    <LeadEditor
      lead={leadQ.data}
      onRefresh={() => {
        void qc.invalidateQueries({ queryKey: ['inspection-lead', id] })
        void qc.invalidateQueries({ queryKey: ['lead-activity', id] })
        void qc.invalidateQueries({ queryKey: ['inspection-leads'] })
        void qc.invalidateQueries({ queryKey: ['lead-stage-counts'] })
      }}
    />
  )
}

function LeadEditor({
  lead,
  onRefresh,
}: {
  lead: InspectionLeadRow
  onRefresh: () => void
}) {
  const { t } = useTranslation()
  const ui = leadPipelineUi(lead.pipeline)
  // Тестовый лид: галочка или марка на «тест» (как lead_is_test в БД).
  const testByBrand = /^\s*тест/i.test(lead.vehicle_brand ?? '')
  const testMut = useMutation({
    mutationFn: (v: boolean) => updateInspectionLead(lead.id, { is_test: v }),
    onSuccess: onRefresh,
  })
  const [draft, setDraft] = useState<LeadDraft>(() => toLeadDraft(lead))
  // Лид обновился в фоне (Автотека, Realtime, другой оператор) — подтягиваем
  // в draft поля, которые оператор не трогал. Иначе на экране старые значения,
  // а «Сохранить» затёр бы свежие данные в БД.
  const [syncedLead, setSyncedLead] = useState(lead)
  if (lead !== syncedLead) {
    const before = toLeadDraft(syncedLead)
    const after = toLeadDraft(lead)
    setSyncedLead(lead)
    setDraft((d) => {
      const next = { ...d }
      for (const k of Object.keys(after) as (keyof LeadDraft)[]) {
        if (d[k] === before[k]) next[k] = after[k]
      }
      return next
    })
  }
  const patch = useMemo(() => diffLeadDraft(draft, lead), [draft, lead])
  const dirty = Object.keys(patch).length > 0

  const navigate = useNavigate()
  const saveMut = useMutation({
    mutationFn: (p: Record<string, unknown>) => updateInspectionLead(lead.id, p),
    onSuccess: onRefresh,
  })

  // Авто-сохранение грязного draft'а перед любым «уходящим» действием
  // палитры (SMS, открыть форму/статус). Иначе оператор печатает в полях,
  // жмёт «Отправить SMS с заявкой» — клиенту улетает старая ссылка с
  // несохранёнными правками лида в БД.
  const flushDraft = async () => {
    if (Object.keys(patch).length === 0) return
    await saveMut.mutateAsync(patch)
  }

  const [paletteError, setPaletteError] = useState<string | null>(null)
  const [showCancelDialog, setShowCancelDialog] = useState(false)

  // Переход в этап автозапроса Автотеки: триггер в БД шлёт запрос через pg_net
  // с лагом до ~10 с, autoteka_state появится по Realtime. До этого показываем
  // «запрашивается», а не кнопку. Сброс: пришёл autoteka_state или 60 с.
  const [autotekaQueuedAt, setAutotekaQueuedAt] = useState<number | null>(null)
  useEffect(() => {
    if (autotekaQueuedAt == null) return
    const id = window.setTimeout(() => setAutotekaQueuedAt(null), 60_000)
    return () => window.clearTimeout(id)
  }, [autotekaQueuedAt])
  const autotekaQueued = autotekaQueuedAt != null && lead.autoteka_state == null

  // Смена статуса (кроме отмены). 'cancelled' идёт через cancelMut + модалку причины.
  const statusMut = useMutation({
    mutationFn: (s: InspectionLeadStatus) => updateInspectionLead(lead.id, { status: s }),
    onSuccess: (_, s) => {
      if (s === ui.autotekaAutoOnStatus && lead.autoteka_state == null && hasAutotekaVehicleId(lead)) {
        setAutotekaQueuedAt(Date.now())
      }
      onRefresh()
    },
    onError: (e: Error) => setPaletteError(t('leads.detail.statusChangeFailed', { msg: e.message })),
  })

  // Отмена: status='cancelled' + причина из словаря + комментарий, атомарно.
  const cancelMut = useMutation({
    mutationFn: ({ reason, comment }: { reason: CancelReasonCode; comment: string | null }) =>
      updateInspectionLead(lead.id, { status: 'cancelled', cancel_reason: reason, cancel_comment: comment }),
    onSuccess: () => {
      setShowCancelDialog(false)
      onRefresh()
    },
    onError: (e: Error) => setPaletteError(t('leads.detail.statusChangeFailed', { msg: e.message })),
  })

  const handleCancelLead = () => {
    if (lead.status === 'cancelled') return
    setPaletteError(null)
    setShowCancelDialog(true)
  }

  const [enrichResult, setEnrichResult] = useState<string | null>(null)
  const enrichMut = useMutation({
    mutationFn: () => runAutotekaEnrichment(lead.id),
    onSuccess: (r) => {
      onRefresh()
      if (r.status === 'pending') {
        setEnrichResult('⏳ ' + t('leads.detail.sberAutotekaStarted'))
        setTimeout(() => setEnrichResult(null), 20_000)
        return
      }
      setEnrichResult(r.status === 'ok' ? '✅ ' + t('leads.detail.enrichDone') : '❌ ' + humanizeEnrichError(r.reason, r.detail))
      setTimeout(() => setEnrichResult(null), 15_000)
    },
    onError: (e) => setEnrichResult('❌ ' + humanizeEnrichError(undefined, (e as Error).message)),
  })

  // Текущий пользователь — для понимания «лид мой / чужой» в палитре действий.
  const myId = useAuthStore((s) => s.profile?.id ?? null)
  // «Тестовый лид» переключает только админ.
  const isAdmin = useAuthStore((s) => s.profile?.role === 'admin')
  const isAssignedToMe = !!myId && lead.assigned_to === myId

  const claimMut = useMutation({
    mutationFn: () => claimLead(lead.id),
    onSuccess: onRefresh,
    onError: (e: Error) => setPaletteError(e.message),
  })
  const releaseMut = useMutation({
    mutationFn: () => releaseLead(lead.id),
    onSuccess: onRefresh,
    onError: (e: Error) => setPaletteError(e.message),
  })

  const [orderError, setOrderError] = useState<string | null>(null)
  const [confirmCreate, setConfirmCreate] = useState(false)
  const createOrderMut = useMutation({
    mutationFn: () => createOrderFromLead(lead.id),
    onSuccess: (r) => {
      if (r.ok) {
        setConfirmCreate(false)
        onRefresh()
      } else {
        setOrderError(`${r.reason ?? 'error'}${r.detail ? ': ' + r.detail : ''}`)
      }
    },
    onError: (e) => setOrderError((e as Error).message),
  })

  const title = [lead.vehicle_brand, lead.vehicle_model].filter(Boolean).join(' ') || t('leads.detail.noVehicleTitle')
  const statusLabel = (s: string) => t(leadStatusLabelKey('status', s, lead.pipeline, (k) => i18n.exists(k)))

  // Блоки палитры действий: всё, что меняет состояние лида, собрано справа.
  const stageBlock = lead.order_id && !ui.statusEditableAfterOrder ? (
    <>
      <StatusBadge status={lead.status} />
      <div className="mt-2 text-xs text-[color:var(--stryzh-color-text-secondary)]">
        {t('leads.detail.convertedNote')}
      </div>
    </>
  ) : (
    <Select<InspectionLeadStatus>
      value={lead.status}
      onChange={(s) => {
        if (!s) return
        if (s === 'cancelled') {
          setPaletteError(null)
          setShowCancelDialog(true)
        } else {
          statusMut.mutate(s)
        }
      }}
      options={leadStatusOptions(lead).map((s) => ({ value: s, label: statusLabel(s) }))}
    />
  )

  const autotekaBlock = ui.terminal.includes(lead.status) && !isAutotekaWaiting(lead) ? null : (
    <>
      <AutotekaControl
        lead={lead}
        pending={enrichMut.isPending}
        queued={autotekaQueued}
        onRequest={async () => {
          // VIN/госномер/ID объявления могли быть только что поправлены — сохраняем.
          await flushDraft()
          enrichMut.mutate()
        }}
      />
      {enrichResult ? (
        <div className="mt-2 rounded bg-[color:var(--stryzh-color-border-subtle)] px-3 py-2 text-sm text-[color:var(--stryzh-color-text-primary)]">
          {enrichResult}
        </div>
      ) : null}
    </>
  )

  const orderBlock = lead.order_id || lead.status === 'cancelled' ? null : ui.orderFromStatus ? (
    // Заказ создаёт отдел осмотров с нужного этапа; адрес осмотра обязателен.
    <>
      {lead.status === ui.orderFromStatus ? (
        <>
          <Button
            type="button"
            variant="control"
            disabled={createOrderMut.isPending || !draft.inspection_address.trim()}
            onClick={async () => {
              setOrderError(null)
              // Адрес мог быть только что введён — сначала сохраняем.
              await flushDraft()
              createOrderMut.mutate()
            }}
          >
            {createOrderMut.isPending ? t('leads.detail.creating') : t('leads.detail.sberCreateOrder')}
          </Button>
          <div className="mt-2 text-xs text-[color:var(--stryzh-color-text-secondary)]">
            {draft.inspection_address.trim()
              ? t('leads.detail.sberCreateOrderHint')
              : t('leads.detail.sberCreateOrderNeedAddress')}
          </div>
        </>
      ) : (
        <div className="text-sm text-[color:var(--stryzh-color-text-secondary)]">
          {t('leads.detail.sberCreateOrderOnlyOo')}
        </div>
      )}
      {orderError ? <div className="mt-2 text-xs text-red-300">{orderError}</div> : null}
    </>
  ) : (
    // Клиент платит сам: заказ после оплаты либо принудительно без оплаты.
    <>
      {lead.payment_at ? (
        <Button
          type="button"
          variant="control"
          disabled={createOrderMut.isPending}
          onClick={() => createOrderMut.mutate()}
        >
          {createOrderMut.isPending ? t('leads.detail.creating') : t('leads.detail.createOrder')}
        </Button>
      ) : !confirmCreate ? (
        <Button
          type="button"
          variant="control"
          onClick={() => {
            setOrderError(null)
            setConfirmCreate(true)
          }}
        >
          {t('leads.detail.createOrderNoPay')}
        </Button>
      ) : (
        <div className="space-y-2">
          <div className="text-sm text-[color:var(--stryzh-color-text-secondary)]">
            {t('leads.detail.createOrderNoPayConfirm')}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="control"
              disabled={createOrderMut.isPending}
              onClick={() => createOrderMut.mutate()}
            >
              {createOrderMut.isPending ? t('leads.detail.creating') : t('leads.detail.createNoPay')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={createOrderMut.isPending}
              onClick={() => setConfirmCreate(false)}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}
      {orderError ? <div className="mt-2 text-xs text-red-300">{orderError}</div> : null}
    </>
  )

  return (
    <div className="w-full max-w-none px-6 py-4 lg:px-8">
      <CancelLeadDialog
        open={showCancelDialog}
        pending={cancelMut.isPending}
        onConfirm={(reason, comment) => cancelMut.mutate({ reason, comment })}
        onClose={() => setShowCancelDialog(false)}
      />
      <Breadcrumbs
        items={[
          { label: t('leads.title'), to: '/inspection-leads' },
          { label: title },
        ]}
      />

      <header className="mb-6 flex flex-col gap-2">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4 sm:gap-y-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="min-w-0 break-words font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-hero)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]">
              {title}
            </h1>
            {leadIsTest(lead) ? <TestBadge /> : null}
            <span className="font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-lg)] text-[color:var(--stryzh-color-text-secondary)]">
              {t('leads.detail.leadNo', { no: lead.lead_no })}
            </span>
            <StatusBadge status={lead.status} />
            <AssignedBadge
              assignedTo={lead.assigned_to}
              assignedToName={lead.assigned_to_name ?? null}
              assignedAt={lead.assigned_at}
              isMine={isAssignedToMe}
            />
            {lead.respond_contact_id ? <CarlinkBadge /> : null}
            <LeadRelationBadge lead={lead} />
          </div>
          <div className="flex w-full items-center gap-2 sm:w-auto sm:shrink-0 sm:flex-wrap sm:justify-end">
            <Button
              type="button"
              variant="primary"
              className="flex-1 sm:flex-none"
              disabled={!dirty || saveMut.isPending}
              onClick={() =>
                // Кнопка «Сохранить» — сохранить и вернуться в воронку; автосохранение
                // перед действиями палитры (flushDraft) оставляет в карточке.
                saveMut.mutate(patch, { onSuccess: () => navigate('/inspection-leads') })
              }
            >
              {saveMut.isPending ? t('leads.detail.saving') : dirty ? t('common.save') : t('leads.detail.noChanges')}
            </Button>
            {dirty ? (
              <Button
                type="button"
                variant="ghost"
                className="flex-1 sm:flex-none"
                disabled={saveMut.isPending}
                onClick={() => navigate('/inspection-leads')}
              >
                {t('leads.detail.discard')}
              </Button>
            ) : null}
          </div>
        </div>
        <p className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('leads.detail.created')}: {format(new Date(lead.created_at), i18n.language === 'en' ? "d MMMM yyyy 'at' HH:mm" : "d MMMM yyyy 'в' HH:mm", { locale: dateFnsLocale() })}
          {' · '}<LiveAgo from={lead.created_at} />
          {' · '}{t('leads.detail.updated')}: {format(new Date(lead.updated_at), "d MMM HH:mm", { locale: dateFnsLocale() })}
        </p>
        {saveMut.isError ? (
          <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
            {(saveMut.error as Error).message}
          </div>
        ) : null}
      </header>

      <div className="mb-6">
        <CarlinkAiSummary leadId={lead.id} />
      </div>

      {/* Палитра одна: на мобильном идёт первой, на десктопе — над правой
          колонкой (grid: левая колонка занимает обе строки). */}
      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-5 lg:grid-rows-[auto_1fr] lg:items-start lg:gap-6">
        <div className="order-1 min-w-0 lg:order-none lg:col-span-2 lg:col-start-4 lg:row-start-1">
          <LeadWorkPalette
            lead={lead}
            isAssignedToMe={isAssignedToMe}
            onClaim={() => { setPaletteError(null); claimMut.mutate() }}
            onRelease={() => { setPaletteError(null); releaseMut.mutate() }}
            claimPending={claimMut.isPending}
            releasePending={releaseMut.isPending}
            onCancel={handleCancelLead}
            cancelPending={cancelMut.isPending}
            error={paletteError}
            flushDraft={flushDraft}
            stage={stageBlock}
            autoteka={autotekaBlock}
            order={orderBlock}
            manageExtra={
              isAdmin ? (
                <div className="flex flex-col gap-1">
                  <PillToggle
                    checked={Boolean(lead.is_test) || testByBrand}
                    onChange={(v) => {
                      if (!testByBrand && !testMut.isPending) testMut.mutate(v)
                    }}
                    label={t('leads.detail.isTest')}
                  />
                  <span className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
                    {testByBrand ? t('leads.detail.isTestByBrand') : t('leads.detail.isTestHint')}
                  </span>
                  {testMut.isError ? (
                    <span className="text-xs text-red-300">{(testMut.error as Error).message}</span>
                  ) : null}
                </div>
              ) : null
            }
          />
        </div>

        {/* Левая колонка — данные лида */}
        <div className="order-3 flex min-w-0 flex-col gap-4 lg:order-none lg:col-span-3 lg:col-start-1 lg:row-span-2 lg:row-start-1">
          <section className={cardCls}>
            <h2 className="stryzh-heading-card mb-4">{t('leads.detail.secClient')}</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label={t('leads.detail.inpName')}
                value={draft.client_name}
                onChange={(e) => setDraft((d) => ({ ...d, client_name: e.target.value }))}
              />
              <Input
                label={t('leads.detail.inpPhone')}
                value={draft.client_phone}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, client_phone: formatPhoneInput(d.client_phone, e.target.value) }))
                }
                inputMode="tel"
                placeholder="+7 999 123 45 67"
              />
            </div>
            <div className="mt-3">
              <Input
                label={t('leads.detail.inpLocation')}
                value={draft.location}
                onChange={(e) => setDraft((d) => ({ ...d, location: e.target.value }))}
              />
            </div>
          </section>

          {ui.sections.partner ? <LeadPartnerSection lead={lead} draft={draft} setDraft={setDraft} /> : null}

          <section className={cardCls}>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h2 className="stryzh-heading-card">{t('leads.detail.secVehicle')}</h2>
              <div className="flex flex-wrap items-center gap-2">
                {lead.autoteka_web_url ? (
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => window.open(lead.autoteka_web_url!, '_blank', 'noopener')}
                    >
                      {t('leads.detail.autotekaOnline')}
                    </Button>
                    <CopyAutotekaLinkButton url={lead.autoteka_web_url} />
                  </>
                ) : null}
                {lead.autoteka_web_url && lead.autoteka_report_id ? (
                  <AutotekaPdfButton leadId={lead.id} storedPath={lead.autoteka_pdf_path} />
                ) : null}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label={t('leads.detail.inpMake')}
                value={draft.vehicle_brand}
                onChange={(e) => setDraft((d) => ({ ...d, vehicle_brand: e.target.value }))}
              />
              <Input
                label={t('leads.detail.inpModel')}
                value={draft.vehicle_model}
                onChange={(e) => setDraft((d) => ({ ...d, vehicle_model: e.target.value }))}
              />
              <Input
                label={t('leads.detail.inpYear')}
                inputMode="numeric"
                value={draft.vehicle_year}
                onChange={(e) => setDraft((d) => ({ ...d, vehicle_year: e.target.value }))}
              />
              <Input
                label={t('leads.detail.inpVin')}
                value={draft.vehicle_vin}
                onChange={(e) => setDraft((d) => ({ ...d, vehicle_vin: e.target.value.toUpperCase() }))}
              />
              <Input
                label={t('leads.detail.inpPlate')}
                value={draft.vehicle_license_plate}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, vehicle_license_plate: e.target.value.toUpperCase() }))
                }
              />
              {ui.sections.itemId ? (
                <Input
                  label={t('leads.detail.inpAvitoItemId')}
                  inputMode="numeric"
                  value={draft.item_id}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, item_id: e.target.value.replace(/[^\d]/g, '') }))
                  }
                  placeholder={t('leads.detail.phItemId')}
                />
              ) : null}
            </div>
          </section>

          <LeadCommunicationsSection leadId={lead.id} clientPhone={lead.client_phone} leadCreatedAt={lead.created_at} />

          <TeamChatFeed leadId={lead.id} className={cardCls} />

          {ui.sections.sellerAd ? (
            <section className={cardCls}>
              <h2 className="stryzh-heading-card mb-4">{t('leads.detail.secSellerAd')}</h2>
              <div>
                <Input
                  label={t('leads.detail.inpListingLink')}
                  value={draft.item_link}
                  onChange={(e) => setDraft((d) => ({ ...d, item_link: e.target.value }))}
                />
                {lead.item_link ? (
                  <div className="mt-2 text-xs">
                    <a
                      href={lead.item_link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[color:var(--stryzh-color-accent-orange)] hover:underline"
                    >
                      {t('leads.detail.openOnAvito')}
                    </a>
                  </div>
                ) : null}
              </div>
              {lead.client_comment ? (
                <div className="mt-3">
                  <div className="stryzh-input-label">{t('leads.detail.clientCommentLabel')}</div>
                  <div className="mt-1 whitespace-pre-wrap break-words rounded bg-[color:var(--stryzh-color-border-subtle)] px-3 py-2 text-sm text-[color:var(--stryzh-color-text-primary)]">
                    {lead.client_comment}
                  </div>
                </div>
              ) : null}
            </section>
          ) : null}

          {ui.sections.price ? (
            <section className={cardCls}>
              <h2 className="stryzh-heading-card mb-4">{t('leads.detail.secPrice')}</h2>
              <div>
                {lead.payment_at ? (
                  <>
                    <div className="stryzh-input-label">{t('leads.detail.priceValueLabel')}</div>
                    <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
                      {new Intl.NumberFormat('ru-RU').format(lead.price_amount)} ₽{' '}
                      <span className="text-xs text-[color:var(--stryzh-color-text-secondary)]">{t('leads.detail.paidNotEditable')}</span>
                    </div>
                  </>
                ) : (
                  <Input
                    label={t('leads.detail.priceInputLabel')}
                    inputMode="numeric"
                    value={draft.price_amount}
                    onChange={(e) => setDraft((d) => ({ ...d, price_amount: e.target.value.replace(/[^\d]/g, '') }))}
                    hint={t('leads.detail.priceHint')}
                  />
                )}
              </div>
            </section>
          ) : null}

          <LeadDispatcherComments leadId={lead.id} />

          {ui.sections.payment ? <LeadPaymentCard lead={lead} onAfterRefund={onRefresh} /> : null}
        </div>

        {/* Правая колонка — заказ / торги / отмена / согласия / активность */}
        <div className="order-2 flex min-w-0 flex-col gap-4 lg:order-none lg:col-span-2 lg:col-start-4 lg:row-start-2">
          {lead.order_id ? (
            <section className={cardCls}>
              <h2 className="stryzh-heading-card mb-4">{t('leads.detail.secOrder')}</h2>
              <div className="mb-3 text-sm text-[color:var(--stryzh-color-text-primary)]">
                {t('leads.detail.orderStatusPrefix')} {lead.order_status ? statusLabelRu(lead.order_status as OrderStatus) : '—'}
              </div>
              {lead.order_inspection_at ? (
                <div className="mb-3 text-sm text-[color:var(--stryzh-color-text-primary)]">
                  {t('leads.detail.orderInspectionAt')} {formatMoscowDateTime(lead.order_inspection_at)}
                </div>
              ) : null}
              <Link
                to={`/orders/${lead.order_id}`}
                className="text-[color:var(--stryzh-color-accent-orange)] hover:underline"
              >
                {t('leads.detail.openOrder')}
              </Link>
            </section>
          ) : null}

          {ui.sections.carlink ? <CarlinkAuctionCard leadId={lead.id} className={cardCls} /> : null}

          {lead.status === 'cancelled' && (lead.cancel_reason || lead.cancel_comment) ? (
            <section className={cardCls}>
              <h2 className="stryzh-heading-card mb-3">{t('leads.detail.secCancel')}</h2>
              <div className="text-sm text-[color:var(--stryzh-color-text-primary)]">
                {lead.cancel_reason ? t(`leads.cancelReason.${lead.cancel_reason}`) : '—'}
              </div>
              {lead.cancel_comment ? (
                <div className="mt-2 whitespace-pre-wrap break-words text-sm text-[color:var(--stryzh-color-text-secondary)]">
                  {lead.cancel_comment}
                </div>
              ) : null}
            </section>
          ) : null}

          {ui.sections.consents ? (
            <section className={cardCls}>
              <h2 className="stryzh-heading-card mb-4">{t('leads.detail.secConsents')}</h2>
              <div className="space-y-1 text-xs text-[color:var(--stryzh-color-text-secondary)]">
                <div>
                  {t('leads.detail.consentPd')}{' '}
                  {lead.pd_consent_at
                    ? format(new Date(lead.pd_consent_at), i18n.language === 'en' ? "d MMM yyyy 'at' HH:mm" : "d MMM yyyy 'в' HH:mm", { locale: dateFnsLocale() })
                    : <span className="text-[color:var(--stryzh-color-text-secondary)]">{t('leads.detail.consentNotGiven')}</span>}
                </div>
                <div>
                  {t('leads.detail.consentOffer')}{' '}
                  {lead.offer_consent_at
                    ? format(new Date(lead.offer_consent_at), i18n.language === 'en' ? "d MMM yyyy 'at' HH:mm" : "d MMM yyyy 'в' HH:mm", { locale: dateFnsLocale() })
                    : <span className="text-[color:var(--stryzh-color-text-secondary)]">{t('leads.detail.consentNotGiven')}</span>}
                </div>
              </div>
            </section>
          ) : null}

          <section className={cardCls}>
            <LeadActivitySection leadId={lead.id} />
          </section>

          {ui.sections.avitoApi ? <LeadAvitoApiCalls leadId={lead.id} /> : null}
        </div>
      </div>
    </div>
  )
}
